import { CompositeScreenProps } from '@react-navigation/native'
import VersionCheck from 'expo-react-native-version-checker'
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Linking } from 'react-native'

import { EventList } from 'partials/EventList'
import { NavMenuButton } from 'partials/MobileNav/types'
import { Page } from 'partials/Page'

import { Box } from 'widgets/Box'
import { ScrollableTabView } from 'widgets/TabView'

import { useClientSubscriptions } from 'hooks/useClientSubscriptions'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useGroup } from 'hooks/useGroup'
import { useGroupEvents } from 'hooks/useGroupEvents'
import { useOtherMembers } from 'hooks/useOtherMembers'
import { usePublicEvents } from 'hooks/usePublicEvents'
import { useQueryClient } from 'hooks/useQueryClient'
import { useUserCreatedEvents } from 'hooks/useUserCreatedEvents'
import { useUserEvents } from 'hooks/useUserEvents'

import { links, platform } from 'utils/constants'

import { useFocusMode } from 'contexts/FocusModeContext'

import { Event } from 'types/event'

import { FeedEmptyState } from './FeedEmptyState'
import { FeedHeader } from './FeedHeader'

export const FeedPage = ({ navigation }: CompositeScreenProps<any, any>) => {
  const { data: currentUser } = useCurrentUser()
  const queryClient = useQueryClient()

  const [isCommentLoading, setIsCommentLoading] = useState(false)

  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])

  const { data: members } = useOtherMembers(focusMode.group)

  const { data: subscriptions } = useClientSubscriptions({
    userId: currentUser?.id,
  })

  const subscribedGroupIds = useMemo(
    () => (subscriptions || []).map((sub) => sub.program.id || ''),
    [subscriptions],
  )

  const { data: userGroup } = useGroup({ userId: currentUser?.id })

  const eventGroupIds = useMemo(
    () => (userGroup ? [userGroup.id, ...subscribedGroupIds] : subscribedGroupIds),
    [subscribedGroupIds, userGroup],
  )

  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)

  const {
    data: userEventsData,
    isLoading: isUserEventsLoading,
    fetchNextPage: fetchNextUserEvents,
    isFetchingNextPage: isUserEventsFetchingNextPage,
  } = useUserEvents({
    userId: focusMode.group ? undefined : currentUser?.id,
    eventObject: 'post',
  })
  const userEvents = useMemo(
    () => userEventsData?.pages.reduce((acc, page) => acc.concat(page), []),
    [userEventsData?.pages],
  )

  const {
    data: userCreatedEventsData,
    isLoading: isUserCreatedEventsLoading,
    fetchNextPage: fetchNextUserCreatedEvents,
    isFetchingNextPage: isUserCreatedEventsFetchingNextPage,
  } = useUserCreatedEvents({ userId: focusMode.group ? undefined : currentUser?.id })

  const userCreatedEvents = useMemo(
    () => userCreatedEventsData?.pages.reduce((acc, page) => acc.concat(page), []),
    [userCreatedEventsData?.pages],
  )

  const {
    data: groupEventsData,
    isLoading: isGroupEventsLoading,
    fetchNextPage: fetchNextGroupEvents,
    isFetchingNextPage: isGroupEventsFetchingNextPage,
  } = useGroupEvents({
    groupIds: focusMode.group ? [focusMode.group.id] : (eventGroupIds as string[]),
  })

  const groupEvents = useMemo(
    () => groupEventsData?.pages.reduce((acc, page) => acc.concat(page), []),
    [groupEventsData?.pages],
  )

  const isEventsLoading = useMemo(
    () => isUserEventsLoading || isUserCreatedEventsLoading || isGroupEventsLoading,
    [isGroupEventsLoading, isUserCreatedEventsLoading, isUserEventsLoading],
  )

  const isEventsFetchingNextPage = useMemo(
    () =>
      isUserEventsFetchingNextPage ||
      isUserCreatedEventsFetchingNextPage ||
      isGroupEventsFetchingNextPage,
    [
      isGroupEventsFetchingNextPage,
      isUserCreatedEventsFetchingNextPage,
      isUserEventsFetchingNextPage,
    ],
  )

  const fetchNextEvents = () => {
    setIsFetchingNextPage(true)

    fetchNextUserEvents()
    fetchNextUserCreatedEvents()
    fetchNextGroupEvents()
  }

  const events = useMemo(() => {
    let finalEvents: Array<Event> = []
    finalEvents = finalEvents.concat(userEvents ?? [])
    finalEvents = finalEvents.concat(userCreatedEvents ?? [])
    finalEvents = finalEvents.concat(groupEvents ?? [])
    finalEvents = finalEvents.filter(
      (event) =>
        (event.eventType === 'create' || event.eventType === 'completion') &&
        !event.post?.isArchived &&
        !event.post?.isPending,
    )

    // It's possible that each query returned some of the same events.
    // We wouldn't want to display them twice.
    // This could also cause a crash because the underlying iOS AV Player cannot
    // load twice from the same source.
    const uniqueIds: string[] = []
    finalEvents = finalEvents.filter((event) => {
      const isDuplicate = uniqueIds.includes(event.id)

      if (!isDuplicate) {
        uniqueIds.push(event.id)

        return true
      }

      return false
    })

    finalEvents.sort((a, b) => {
      return b.created - a.created
    })

    setIsFetchingNextPage(false)
    return finalEvents
  }, [groupEvents, userCreatedEvents, userEvents])

  const isEventsRefreshing = useMemo(
    () => !events || (isEventsLoading && !isEventsFetchingNextPage),
    [events, isEventsFetchingNextPage, isEventsLoading],
  )

  const {
    data: publicPostEvents,
    isLoading: isPublicEventsLoading,
    fetchNextPage: fetchNextPublicEvents,
    isFetchingNextPage: isPublicEventsFetchingNextPage,
  } = usePublicEvents({})

  const publicEvents = useMemo(() => {
    let finalEvents: Array<Event> = []
    finalEvents = publicPostEvents.filter(
      (event) => !!event.viewers.users && event.viewers.users.length > 0,
    )
    finalEvents = finalEvents.filter(
      (event) =>
        (event.eventType === 'create' || event.eventType === 'completion') &&
        !event.post?.isArchived &&
        !event.post?.isPending,
    )
    return finalEvents
  }, [publicPostEvents])

  const isPublicEventsRefreshing = useMemo(
    () => !publicEvents || (isPublicEventsLoading && !isPublicEventsFetchingNextPage),
    [isPublicEventsFetchingNextPage, isPublicEventsLoading, publicEvents],
  )

  const onRefresh = useCallback(() => {
    queryClient.invalidateQueries(['groupEvents'])
    queryClient.invalidateQueries(['userEvents'])
    queryClient.invalidateQueries(['userCreatedEvents'])
    queryClient.invalidateQueries(['publicEvents'])
    queryClient.invalidateQueries(['posts', null, 'created', null, false, 25, 'Workouts']) // Recent Workouts
    queryClient.invalidateQueries(['discoverContent']) // Main dependency for Top Groups (Emerging Coaches)
    queryClient.invalidateQueries(['leaderboard'])
  }, [queryClient])

  const headerComponent = (
    <Box>
      <FeedHeader />
    </Box>
  )

  const hasContent: boolean = useMemo(
    () =>
      (events && events?.length > 0) ||
      !isFocusMode ||
      (isFocusMode && !!members && members?.length > 0),
    [events, isFocusMode, members],
  )

  const [isUpdateNeeded, setIsUpdatedNeeded] = useState(false)
  const checkUpdateNeeded = async (): Promise<void> => {
    const updateNeeded = await VersionCheck('US').needUpdate()
    if (updateNeeded && updateNeeded.isNeeded) {
      setIsUpdatedNeeded(true)
    }
  }

  useEffect(() => {
    queryClient.prefetchQuery(['activityTypes'])
  }, [queryClient])

  useEffect(() => {
    checkUpdateNeeded()
  }, [])

  const tabMap: Record<string, ReactNode> = {
    Following: (
      <>
        <EventList
          events={events}
          fetchNextEvents={fetchNextEvents}
          loading={isEventsLoading}
          refreshing={isEventsRefreshing}
          onRefresh={onRefresh}
          noContentMessage='There have been no posts by anyone you follow or any coaches you are subscribed to yet.'
          setIsCommentLoading={setIsCommentLoading}
          isFetchingNextPage={isFetchingNextPage}
        />
      </>
    ),
    Discovery: (
      <EventList
        events={publicEvents}
        fetchNextEvents={fetchNextPublicEvents}
        loading={isPublicEventsLoading}
        refreshing={isPublicEventsRefreshing}
        onRefresh={onRefresh}
        noContentMessage='There have been no posts by anyone yet.'
        setIsCommentLoading={setIsCommentLoading}
        isFetchingNextPage={isPublicEventsFetchingNextPage}
      />
    ),
  }

  const testButton: NavMenuButton = {
    title: 'TEST',
    onPress: () => {
      navigation.navigate(links.PROGRAM_PAGE, {})
    },
  }

  const { isProgramsFlagEnabled } = useFeatureFlags()

  return (
    <Page
      isLoading={isCommentLoading}
      title={!isFocusMode ? 'Upspace' : focusMode.group?.title}
      withNav
      withLogoButton
      withExitFocusMode
      navigation={navigation}
      backgroundColor='white'
      withNotificationButton={!isFocusMode}
      withChatButton
      withSearchButton
      navbarRightButtons={isProgramsFlagEnabled ? [testButton] : []}
      bannerText={isUpdateNeeded ? "⚡ We've updated the app!" : undefined}
      bannerButtonText='Download Now!'
      // TODO: Get Play Store Link
      bannerButtonOnPress={() =>
        platform.IS_IOS ? Linking.openURL('https://apps.apple.com/us/app/upspace/id1620955864') : {}
      }
    >
      <Box flex={1}>
        {!isFocusMode ? (
          <ScrollableTabView tabs={Object.keys(tabMap)} header={headerComponent}>
            {Object.values(tabMap)}
          </ScrollableTabView>
        ) : (
          <>
            {!hasContent ? (
              <FeedEmptyState />
            ) : (
              <>
                <EventList
                  events={events}
                  fetchNextEvents={fetchNextEvents}
                  loading={isEventsLoading}
                  refreshing={isEventsRefreshing}
                  onRefresh={onRefresh}
                  headerComponent={headerComponent}
                  noContentMessage='There have been no posts by anyone you follow or any coaches you are subscribed to yet.'
                  setIsCommentLoading={setIsCommentLoading}
                  isFetchingNextPage={isFetchingNextPage}
                />
              </>
            )}
          </>
        )}
      </Box>
    </Page>
  )
}
